/** Affinements mobile v3 — ordre de lecture, hauteurs, éléments masqués (PC inchangé). */

const FULL = 48;

const MOBILE_PADDING_Y = {
  announcement: 0,
  header: 0,
  'hero-dark': 0,
  intro: 72,
  forest: 0,
  plats: 72,
  quote: 52,
  'gallery-pad': 72,
  reservation: 72,
  infos: 72,
  footer: 28,
};

const MOBILE_HIDE_STYLES = new Set([
  'jardins-hero-side',
  'jardins-hero-bottom-left',
  'jardins-stamp',
  'jardins-reservation-note',
]);

function stackRow(comp, row, rowSpan) {
  return {
    ...comp,
    mobile: {
      ...(comp.mobile || {}),
      colStart: 1,
      colSpan: FULL,
      row,
      rowSpan,
      hidden: undefined,
    },
  };
}

function hideMobile(comp) {
  return {
    ...comp,
    mobile: { ...(comp.mobile || {}), hidden: true },
  };
}

function maxMobileEnd(components) {
  return (components || []).reduce((max, c) => {
    if (c.mobile?.hidden) return max;
    const m = c.mobile;
    if (!m) return max;
    return Math.max(max, (Number(m.row) || 0) + (Number(m.rowSpan) || 1));
  }, 0);
}

function byLayout(comps) {
  return [...comps].sort((a, b) => (Number(a.layout) || 0) - (Number(b.layout) || 0));
}

function mergePlaced(components, placedList) {
  const map = new Map(placedList.map((c) => [c.id, c]));
  return components.map((c) => {
    if (map.has(c.id)) return map.get(c.id);
    if (MOBILE_HIDE_STYLES.has(c.props?.kitStyle)) return hideMobile(c);
    return hideMobile(c);
  });
}

function stackBySpecs(components, specs) {
  let row = 0;
  const placed = [];
  for (const [kit, span, type] of specs) {
    const c = components.find(
      (x) =>
        x.props?.kitStyle === kit && (type == null || x.type === type),
    );
    if (!c) continue;
    placed.push(stackRow(c, row, span));
    row += span;
  }
  return { components: mergePlaced(components, placed), mobileGridRows: row + 2 };
}

function refineForestMobile(components) {
  return stackBySpecs(components, [
    ['jardins-fill', 10, 'image'],
    ['jardins-eyebrow-light', 2, 'text'],
    ['jardins-seasonal-h2', 7, 'text'],
    ['jardins-body', 5, 'text'],
    ['jardins-seasonal-tags', 4, 'text'],
    ['jardins-btn-outline', 3, 'button'],
  ]);
}

function refinePlatsMobile(components) {
  const imgs = byLayout(components.filter((c) => c.props?.kitStyle === 'jardins-dish'));
  const metas = byLayout(components.filter((c) => c.props?.kitStyle === 'jardins-dish-meta'));
  const titles = byLayout(components.filter((c) => c.props?.kitStyle === 'jardins-dish-title'));
  const descs = byLayout(components.filter((c) => c.props?.kitStyle === 'jardins-dish-desc'));

  let row = 0;
  const placed = [];
  const place = (comp, span) => {
    if (!comp) return;
    placed.push(stackRow(comp, row, span));
    row += span;
  };

  place(components.find((c) => c.props?.kitStyle === 'jardins-eyebrow'), 2);
  place(components.find((c) => c.props?.kitStyle === 'jardins-h2'), 9);
  place(components.find((c) => c.props?.kitStyle === 'jardins-heading-note'), 7);

  for (let i = 0; i < 3; i += 1) {
    place(imgs[i], 15);
    place(metas[i], 2);
    place(titles[i], 3);
    place(descs[i], 4);
  }
  place(components.find((c) => c.props?.kitStyle === 'jardins-disclaimer'), 2);

  return { components: mergePlaced(components, placed), mobileGridRows: row + 2 };
}

function refineIntroMobile(components) {
  return stackBySpecs(components, [
    ['jardins-intro-photo', 20, 'image'],
    ['jardins-eyebrow', 2, 'text'],
    ['jardins-h2', 9, 'text'],
    ['jardins-body', 9, 'text'],
    ['jardins-underline-link', 2, 'text'],
    ['jardins-photo-caption', 2, 'text'],
  ]);
}

function refineHeroMobile(components) {
  return stackBySpecs(components, [
    ['jardins-eyebrow-light', 2, 'text'],
    ['jardins-hero-title', 12, 'text'],
    ['jardins-hero-copy', 5, 'text'],
    ['jardins-btn-cream', 3, 'button'],
    ['jardins-text-link-light', 3, 'button'],
  ]);
}

function refineReservationMobile(components) {
  let row = 0;
  const placed = [];
  const textSpecs = [
    ['jardins-eyebrow-light', 2],
    ['jardins-reservation-title', 9],
    ['jardins-body', 4],
  ];
  for (const [kit, span] of textSpecs) {
    const c = components.find((x) => x.props?.kitStyle === kit);
    if (c) {
      placed.push(stackRow(c, row, span));
      row += span;
    }
  }
  const resa = components.find((c) => c.type === 'reservation');
  if (resa) {
    placed.push(stackRow(resa, row, 32));
    row += 32;
  }
  return { components: mergePlaced(components, placed), mobileGridRows: row + 2 };
}

export function resolveJardinsSectionPadding(section, viewport) {
  if (section?.designKit !== 'jardins' || !viewport || viewport === 'desktop') {
    return section?.paddingY;
  }
  if (viewport === 'mobile' && section.jardinsMobilePaddingY != null) {
    return section.jardinsMobilePaddingY;
  }
  if (viewport === 'tablet' && section.jardinsTabletPaddingY != null) {
    return section.jardinsTabletPaddingY;
  }
  if (viewport === 'mobile') {
    const tone = section.kitSectionTone;
    let key = tone;
    if (!key) {
      if (section.anchor === 'histoire') key = 'intro';
      else if (section.anchor === 'plats') key = 'plats';
      else if (section.anchor === 'infos') key = 'infos';
    }
    if (key && MOBILE_PADDING_Y[key] != null) return MOBILE_PADDING_Y[key];
  }
  return section?.paddingY;
}

export function refineJardinsSectionMobile(section, presetKey) {
  if (!section?.designKit || section.designKit !== 'jardins') return section;

  const components = [...(section.components || [])];
  let result = { components, mobileGridRows: Number(section.jardinsMobileGridRows) || 0 };

  if (presetKey === 'forest') result = refineForestMobile(components);
  else if (presetKey === 'plats') result = refinePlatsMobile(components);
  else if (presetKey === 'intro') result = refineIntroMobile(components);
  else if (presetKey === 'hero-dark') result = refineHeroMobile(components);
  else if (presetKey === 'reservation') result = refineReservationMobile(components);
  else {
    const withHides = components.map((c) =>
      MOBILE_HIDE_STYLES.has(c.props?.kitStyle) ? hideMobile(c) : c,
    );
    result = {
      components: withHides,
      mobileGridRows: Math.max(Number(section.jardinsMobileGridRows) || 0, maxMobileEnd(withHides) + 2),
    };
  }

  const paddingKey =
    presetKey ||
    section.kitSectionTone ||
    (section.anchor === 'histoire' ? 'intro' : section.anchor);
  const mobilePad = MOBILE_PADDING_Y[paddingKey];

  return {
    ...section,
    components: result.components,
    jardinsMobileGridRows: result.mobileGridRows,
    ...(mobilePad != null ? { jardinsMobilePaddingY: mobilePad } : {}),
  };
}
